const CATEGORY_LABELS: Record<string, string> = {
  official_documents: "Official documents",
  site_images: "Site images",
  site_documents: "Site documents",
  incident_documents: "Incident documents",
  incident_audio: "Incident audio",
  mass_grave_images: "Mass grave images",
  mass_grave_documents: "Mass grave documents",
  mass_grave_audio: "Mass grave audio",
};

type EvidenceBranchItem = {
  groupLabel?: string | null;
  slotLabel?: string | null;
  itemIndex?: number | null;
  detailIndex?: number | null;
};

type EvidenceLabelItem = EvidenceBranchItem & {
  category: string;
  captionDefault: string | null;
};

type EvidenceEntryItem = EvidenceLabelItem & {
  mediaType: string;
};

type EvidenceSummaryItem = {
  category: string;
  groupLabel?: string | null;
};

type EvidenceOverlayItem = EvidenceBranchItem & {
  captionDefault: string | null;
  category?: string | null;
};

function humanizeEvidenceToken(value: string): string {
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function looksLikeFilename(value: string | null): boolean {
  return Boolean(value && /^[^\/]+\.[a-z0-9]{2,5}$/i.test(value.trim()));
}

function buildBranchSlotLabel(item: EvidenceBranchItem): string | null {
  const groupLabel = item.groupLabel?.trim();
  const slotLabel = item.slotLabel?.trim();
  const itemSuffix = Number.isFinite(item.itemIndex) ? ` #${(item.itemIndex ?? 0) + 1}` : "";
  const detailSuffix = Number.isFinite(item.detailIndex) ? ` (Detail ${Number(item.detailIndex) + 1})` : "";

  if (groupLabel && slotLabel) {
    return `${groupLabel}${itemSuffix}: ${slotLabel}${detailSuffix}`;
  }
  if (slotLabel) {
    return `${slotLabel}${detailSuffix}`;
  }
  if (groupLabel) {
    return `${groupLabel}${itemSuffix}`;
  }
  return null;
}

export function humanizeEvidenceCategory(category: string): string {
  return CATEGORY_LABELS[category] ?? humanizeEvidenceToken(category);
}

export function humanizeEvidenceMediaType(mediaType: string): string {
  return humanizeEvidenceToken(mediaType);
}

export function buildEvidencePreviewLabel(item: EvidenceLabelItem): string {
  const branchLabel = buildBranchSlotLabel(item);
  if (branchLabel) {
    return branchLabel;
  }
  const caption = item.captionDefault?.trim();
  if (caption) {
    return caption;
  }
  return humanizeEvidenceCategory(item.category);
}

export function buildEvidenceEntryLabel(item: EvidenceEntryItem, index: number): string {
  const branchLabel = buildBranchSlotLabel(item);
  if (branchLabel) {
    return branchLabel;
  }
  const caption = item.captionDefault?.trim();
  if (caption && !looksLikeFilename(caption)) {
    return caption;
  }
  const categoryLabel = humanizeEvidenceCategory(item.category);
  const mediaLabel = humanizeEvidenceMediaType(item.mediaType);
  return `${categoryLabel} ${mediaLabel} ${index}`;
}

export function buildEvidenceEntryType(item: EvidenceEntryItem): string {
  const categoryLabel = item.groupLabel?.trim() || humanizeEvidenceCategory(item.category);
  const mediaLabel = item.slotLabel?.trim() || humanizeEvidenceMediaType(item.mediaType);
  return `${categoryLabel} ${mediaLabel}`.trim();
}

export function buildEvidenceGroupItemLabel(item: EvidenceLabelItem): string {
  const branchLabel = buildBranchSlotLabel(item);
  if (branchLabel) {
    return branchLabel;
  }
  const caption = item.captionDefault?.trim();
  if (caption) {
    return caption;
  }
  return humanizeEvidenceCategory(item.category);
}

export function buildEvidenceGroupTitle(groupLabel: string, itemIndex: number | null): string {
  if (Number.isFinite(itemIndex)) {
    return `${groupLabel} #${Number(itemIndex) + 1}`;
  }
  return groupLabel;
}

export function buildEvidenceOverlayTitle(item: EvidenceOverlayItem): string {
  const branchLabel = buildBranchSlotLabel(item);
  if (branchLabel) {
    return branchLabel;
  }
  const caption = item.captionDefault?.trim();
  if (caption) {
    return caption;
  }
  return humanizeEvidenceCategory(item.category ?? "evidence");
}

export function buildEvidenceOverviewLines(
  items: EvidenceSummaryItem[],
  limit = 3,
  formatCount: (value: number) => string = (value) => String(value),
): string[] {
  if (items.length === 0) {
    return [];
  }

  const byGroup = new Map<string, number>();
  for (const item of items) {
    const label = item.groupLabel?.trim() || humanizeEvidenceCategory(item.category);
    byGroup.set(label, (byGroup.get(label) ?? 0) + 1);
  }

  return [...byGroup.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, limit)
    .map(([label, count]) => `${formatCount(count)} ${label}`);
}

export function isDocumentationEvidence(item: EvidenceLabelItem): boolean {
  return item.category.includes("document");
}
